import { useEffect, useRef } from 'react';
import { Modal } from 'bootstrap';
import moment from 'moment';

type Props = { rec: any | null };

const ViewCallAnalysisModal = ({ rec }: Props) => {
    const modalRef = useRef<HTMLDivElement | null>(null);

    // Show/hide when `rec` changes, and log when modal is actually shown
    useEffect(() => {
        const el = modalRef.current;
        if (!el) return;

        const instance = Modal.getOrCreateInstance(el);

        const onShown = () => {
            console.log('rec in modal (on show):', rec);
            if (rec?.caller_id !== undefined) {
                console.log('caller_id:', rec.caller_id);
            }
        };
        el.addEventListener('shown.bs.modal', onShown);

        if (rec) {
            // wait a tick so the DOM paints with the new `rec` before showing
            requestAnimationFrame(() => instance.show());
        } else {
            instance.hide();
        }

        return () => {
            el.removeEventListener('shown.bs.modal', onShown);
        };
    }, [rec]);

    // Safe JSON parse for summary_json
    const parseSummary = (val: any) => {
        try {
            const parsed = typeof val === 'string' ? JSON.parse(val) : val;
            return Array.isArray(parsed) ? parsed : null;
        } catch {
            return null;
        }
    };

    const summary = rec ? parseSummary(rec.summary_json) : null;

    return (
        <div
            className="modal fade px-3"
            id="analysisModal"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex={-1}
            aria-labelledby="analysisModalLabel"
            aria-hidden="true"
            ref={modalRef}>
            <div className="modal-dialog modal-dialog-centered max-800 mx-auto">
                <div className="modal-content">
                    <div className="modal-header p-4 height-70 border-0 bg-gray-f2">
                        <h5 className="modal-title" id="analysisModalLabel">
                            Call Analysis Details{' '}
                        </h5>
                        <button type="button" className="btn-close btn-close-small" data-bs-dismiss="modal" aria-label="Close" />
                    </div>

                    <div className="modal-body p-4">
                        {/* Meta Info */}
                        <div className="row mb-4">
                            <div className="col-md-3">
                                <div className="card shadow-sm p-3">
                                    <h6 className="text-muted">Intent</h6>
                                    <p className="fw-bold">{rec?.intent}</p>
                                </div>
                            </div>
                            <div className="col-md-5">
                                <div className="card shadow-sm p-3">
                                    <h6 className="text-muted">Intent Justification</h6>
                                    <p>{rec?.intent_justification}</p>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card shadow-sm p-3 text-center">
                                    <h6 className="text-muted">Quality Score</h6>
                                    <p className="fw-bold">{rec?.quality_score}</p>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card shadow-sm p-3 text-center">
                                    <h6 className="text-muted">Call Time</h6>
                                    <p>{moment(rec?.call_time).format('DD-MMM-YYYY H:mm:ss')}</p>
                                </div>
                            </div>
                        </div>

                        {/* Accordion for details */}
                        <div className="accordion" id="analysisAccordion">
                            {/* Summary */}
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingSummary">
                                    <button
                                        className="accordion-button"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseSummary">
                                        📄 Call Summary
                                    </button>
                                </h2>
                                <div id="collapseSummary" className="accordion-collapse collapse show" data-bs-parent="#analysisAccordion">
                                    <div className="accordion-body">
                                        {(() => {
                                            try {
                                                const summary = JSON.parse(rec?.summary_json);
                                                return (
                                                    <ul className="list-group">
                                                        {summary.map((item: any, i: number) => (
                                                            <li key={i} className="list-group-item">
                                                                <strong>{item.chapter_name}:</strong> {item.chapter_summary}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                );
                                            } catch {
                                                return <p>{rec?.summary_json}</p>;
                                            }
                                        })()}
                                    </div>
                                </div>
                            </div>

                            {/* BANT */}
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingBant">
                                    <button
                                        className="accordion-button collapsed"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseBant">
                                        🎯 BANT Info
                                    </button>
                                </h2>
                                <div id="collapseBant" className="accordion-collapse collapse" data-bs-parent="#analysisAccordion">
                                    <div className="accordion-body">
                                        {(() => {
                                            try {
                                                const bant = JSON.parse(rec?.bant_json);
                                                return (
                                                    <ul className="list-group">
                                                        {Object.entries(bant).map(([key, value], i) => (
                                                            <li key={i} className="list-group-item">
                                                                <strong>{key}:</strong> {String(value)}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                );
                                            } catch {
                                                return <p>{rec?.bant_json}</p>;
                                            }
                                        })()}
                                    </div>
                                </div>
                            </div>

                            {/* Qualification */}
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingQual">
                                    <button
                                        className="accordion-button collapsed"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseQual">
                                        ✅ Qualification Info
                                    </button>
                                </h2>
                                <div id="collapseQual" className="accordion-collapse collapse" data-bs-parent="#analysisAccordion">
                                    <div className="accordion-body">
                                        {(() => {
                                            try {
                                                const qual = JSON.parse(rec?.qualification_json);
                                                return (
                                                    <ul className="list-group">
                                                        {qual.map((q: any, i: number) => (
                                                            <li key={i} className="list-group-item">
                                                                <strong>{q.label}:</strong> {String(q.answer)}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                );
                                            } catch {
                                                return <p>{rec?.qualification_json}</p>;
                                            }
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn-good-qa" data-bs-dismiss="modal">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewCallAnalysisModal;
